// src/store/usePromptStore.js
import {
  createPrompt,
  deletePrompt,
  getWorkspacePrompts,
  updatePrompt,
} from "@/lib/db/utils/supabase/prompts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const STORAGE_KEY = "prompt-storage";

export const usePromptStore = create(
  persist(
    (set, get) => ({
      // State
      prompts: [],
      isLoading: false,
      initialized: false,
      isSubmitting: false,
      searchQuery: "",
      selectedCategory: "all",
      showKeywords: false,
      error: null,

      initializePromptStore: async (workspaceId) => {
        set({ isLoading: true });
        const { data, error } = await getWorkspacePrompts(workspaceId);
        if (error) throw error;
        set({ prompts: [...data] });
        set({ initialized: true, isLoading: false });
      },

      // Computed
      getFilteredPrompts: () => {
        const { prompts, searchQuery, selectedCategory } = get();

        return prompts.filter((prompt) => {
          const matchesSearch = searchQuery
            .toLowerCase()
            .split(" ")
            .every(
              (term) =>
                prompt.title?.toLowerCase().includes(term) ||
                prompt.description?.toLowerCase().includes(term) ||
                prompt.category?.toLowerCase().includes(term) ||
                (prompt.variables &&
                  prompt.variables.some((v) => v.toLowerCase().includes(term)))
            );

          const matchesCategory =
            selectedCategory === "all" || prompt.category === selectedCategory;

          return matchesSearch && matchesCategory;
        });
      },

      // Actions
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSelectedCategory: (category) => set({ selectedCategory: category }),
      setShowKeywords: (show) => set({ showKeywords: show }),

      fetchPrompts: async (workspaceId) => {
        if (!workspaceId) return;

        set({ isLoading: true, error: null });

        try {
          const { data, error } = await getWorkspacePrompts(workspaceId);

          if (error) throw error;

          set({
            prompts: [...data],
          });
        } catch (error) {
          set({ error: error.message });
        } finally {
          set({ isLoading: false });
        }
      },

      addPrompt: async (promptData, workspaceId, userId) => {
        set({ isSubmitting: true, error: null });

        try {
          const { data, error } = await createPrompt({
            ...promptData,
            user_id: userId,
            workspace_id: workspaceId,
          });

          if (error) throw error;

          set((state) => ({
            prompts: [...state.prompts, data],
          }));

          return { success: true };
        } catch (error) {
          set({ error: error.message });
          return { success: false, error: error.message };
        } finally {
          set({ isSubmitting: false });
        }
      },

      updatePrompt: async (id, promptData) => {
        set({ isSubmitting: true, error: null });

        try {
          const { data, error } = await updatePrompt(id, promptData);

          if (error) throw error;

          set((state) => ({
            prompts: state.prompts.map((p) => (p.id === id ? data : p)),
          }));

          return { success: true };
        } catch (error) {
          set({ error: error.message });
          return { success: false, error: error.message };
        } finally {
          set({ isSubmitting: false });
        }
      },

      deletePrompt: async (id) => {
        try {
          const { error } = await deletePrompt(id);

          if (error) throw error;

          set((state) => ({
            prompts: state.prompts.filter((p) => p.id !== id),
          }));

          return { success: true };
        } catch (error) {
          set({ error: error.message });
          return { success: false, error: error.message };
        }
      },

      resetState: () => {
        set({
          prompts: [],
          isLoading: false,
          isSubmitting: false,
          searchQuery: "",
          selectedCategory: "all",
          showKeywords: false,
          error: null,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these keys
        prompts: state.prompts,
        searchQuery: state.searchQuery,
        selectedCategory: state.selectedCategory,
        showKeywords: state.showKeywords,
        initialized: state.initialized,
      }),
      // Add version control for future migrations
      version: 1,
      // Optional: Migrate state when version changes
      migrate: (persistedState, version) => {
        if (version === 0) {
          // Migration logic for future versions
          return {
            ...persistedState,
            prompts: [],
            // Add any new fields or modify existing ones
          };
        }
        return persistedState;
      },
    }
  )
);

export default usePromptStore;
